import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Botao } from './Botao.js';
import { ICONES } from './icones.js';
import { Vazio } from './Vazio.js';

export type PropsSalvaguarda = {
  children: ReactNode;
  /** Quando a tela sabe o que quebrou, diz melhor do que o texto genérico. */
  titulo?: string;
  /** Gancho para registrar o erro (diagnóstico, telemetria). Não substitui
   *  a exibição: a mensagem continua na tela. */
  aoCapturar?: (erro: unknown, info: ErrorInfo) => void;
};

type EstadoSalvaguarda = {
  erro: unknown;
  falhou: boolean;
  /** Muda a cada tentativa e serve de `key` da subárvore: é isso, e não o
   *  `setState` sozinho, que faz o filho ser montado do zero. */
  tentativa: number;
};

/** Uma mensagem sempre sobra, mesmo quando o erro não traz nenhuma: tela em
 *  branco é o que esta classe existe para evitar. */
function mensagemDe(erro: unknown): string {
  if (erro instanceof Error && erro.message !== '') return erro.message;
  if (typeof erro === 'string' && erro !== '') return erro;
  return 'Erro sem mensagem.';
}

export class Salvaguarda extends Component<
  PropsSalvaguarda,
  EstadoSalvaguarda
> {
  override state: EstadoSalvaguarda = {
    erro: null,
    falhou: false,
    tentativa: 0,
  };

  static getDerivedStateFromError(erro: unknown): Partial<EstadoSalvaguarda> {
    return { erro, falhou: true };
  }

  override componentDidCatch(erro: unknown, info: ErrorInfo): void {
    this.props.aoCapturar?.(erro, info);
  }

  private readonly tentarDeNovo = (): void => {
    this.setState((anterior) => ({
      erro: null,
      falhou: false,
      tentativa: anterior.tentativa + 1,
    }));
  };

  override render(): ReactNode {
    const { children, titulo = 'Algo deu errado nesta parte' } = this.props;
    if (!this.state.falhou) {
      // A `key` fica também no caminho feliz: trocá-la é o que remonta, e
      // sem ela aqui o React reaproveitaria o filho quebrado.
      return <div key={this.state.tentativa}>{children}</div>;
    }
    return (
      // `role="alert"` porque a falha acontece depois da carga da tela: sem
      // ele, quem usa VoiceOver não fica sabendo que o conteúdo sumiu.
      <div role="alert">
        {/* `nivel={2}` e a ação larga (prancha 14.3): quando a salvaguarda
            dispara, o que ela mostra ocupa o lugar da tela inteira, e sem
            cabeçalho o VoiceOver não tem marco para chegar até aqui. */}
        <Vazio
          nivel={2}
          titulo={titulo}
          descricao={mensagemDe(this.state.erro)}
          acao={
            <Botao
              variante="secundario"
              largura="cheia"
              onClick={this.tentarDeNovo}
            >
              {/* O ícone vai **dentro** do botão, como na prancha 14.3, e não
                  acima do título: ali ele custaria altura, e o catálogo do
                  design system mede esta seção contra a tela do iPhone. */}
              {ICONES.recarregar}
              Tentar de novo
            </Botao>
          }
        />
      </div>
    );
  }
}
